import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, Outlet, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Navbar from "./component/layout/Navbar";
import Footer from "./component/layout/Footer";
import CookieNotice from "./component/layout/CookieNotice";
import DownloadingModal from "./component/ui/DownloadingModal";
import PasswordGateway, {
    getPasswordGateStatus,
    PASSWORD_GATE_REQUEST_EVENT,
} from "./component/layout/PasswordGateway";
import { useIdleLogout } from "./hooks/useSessionTimers";
import "./styles/stylesheet.scss";
import "./styles/pages/auth/_protected-route.scss";
import "./styles/pages/auth/_password-gateway.scss";

// const INACTIVITY_TIMEOUT_MS = 10 * 1000; // 30 seconds (test)
// const INACTIVITY_WARNING_MS = 8 * 1000; // 15 seconds (test)
const INACTIVITY_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours
const INACTIVITY_WARNING_MS = 5 * 60 * 1000; // 5 minutes
const LAST_ACTIVITY_KEY = "moozLastActivityAt";
const COOKIE_CONSENT_KEY = "moozCookieConsentV1";
const COOKIE_DEBUG_KEY = "moozCookieDebugV1";
const OPEN_COOKIE_SETTINGS_EVENT = "mooz:openCookieSettings";
const DOWNLOAD_STARTED_EVENT = "mooz:downloadStarted";

function readCookieConsent() {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(COOKIE_CONSENT_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || typeof data !== "object") return null;
        return { analytics: data.analytics === true };
    } catch {
        return null;
    }
}

function writeCookieConsent(next) {
    if (typeof window === "undefined") return;
    const payload = {
        version: 1,
        analytics: next.analytics === true,
        updatedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(payload));
}

function readCookieDebug() {
    if (typeof window === "undefined") return false;
    try {
        return window.localStorage.getItem(COOKIE_DEBUG_KEY) === "1";
    } catch {
        return false;
    }
}

function deleteCookie(name) {
    try {
        const expires = "Thu, 01 Jan 1970 00:00:00 GMT";
        const base = `${name}=; expires=${expires}; path=/`;
        document.cookie = base;
        document.cookie = `${base}; samesite=lax`;
        const host = window.location.hostname;
        document.cookie = `${base}; domain=${host}`;
        document.cookie = `${base}; domain=.${host}`;
    } catch {
        /* noop */
    }
}

function clearAnalyticsCookies() {
    deleteCookie("_ga");
    deleteCookie("_gid");
    deleteCookie("_gat");
}

// ─── Inactivity warning ───────────────────────────────────────────────────────
function InactivityWarning({ countdown, msLeft, onStay }) {
    const RADIUS = 16;
    const CIRC = 2 * Math.PI * RADIUS;
    const ratio = Math.max(0, Math.min(1, msLeft / INACTIVITY_WARNING_MS));
    const dash = CIRC * ratio;

    const isUrgent = msLeft <= 5000;

    return (
        <div
            className={`pr-iw${isUrgent ? " pr-iw--urgent" : ""}`}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
        >
            <div className="pr-iw__ring-wrap" aria-hidden="true">
                <svg className="pr-iw__ring" viewBox="0 0 40 40" fill="none">
                    <circle
                        cx="20"
                        cy="20"
                        r={RADIUS}
                        className="pr-iw__ring-track"
                    />
                    <circle
                        cx="20"
                        cy="20"
                        r={RADIUS}
                        className="pr-iw__ring-arc"
                        strokeDasharray={`${dash} ${CIRC}`}
                        strokeDashoffset={CIRC * 0.25}
                    />
                </svg>
                <span className="pr-iw__ring-count">{countdown}</span>
            </div>

            <div className="pr-iw__text">
                <span className="pr-iw__heading">Still there?</span>
                <span className="pr-iw__body">
                    Signing you out in <strong>{countdown}</strong>.
                </span>
            </div>

            <button
                type="button"
                className="pr-iw__btn"
                onClick={onStay}
                autoFocus
            >
                Stay signed in
            </button>
        </div>
    );
}

function App() {
    const location = useLocation();
    const navigate = useNavigate();
    const gaMeasurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    const { isAuthenticated, isLoading, logout, loginWithRedirect } =
        useAuth0();
    const [warningMsLeft, setWarningMsLeft] = useState(null);
    const [cookieConsent, setCookieConsent] = useState(() =>
        readCookieConsent()
    );
    const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);
    const [downloadModalOpen, setDownloadModalOpen] = useState(false);
    const [downloadModalBody, setDownloadModalBody] = useState("");
    const downloadModalTimer = useRef(null);
    const cookieDebugParam =
        typeof window === "undefined"
            ? null
            : new URLSearchParams(location.search).get("cookieDebug");
    const cookieDebug =
        cookieDebugParam === "1"
            ? true
            : cookieDebugParam === "0"
              ? false
              : readCookieDebug();
    const showWarning =
        warningMsLeft !== null && warningMsLeft <= INACTIVITY_WARNING_MS;
    const analyticsAvailable = Boolean(gaMeasurementId);
    const analyticsAllowed = cookieConsent?.analytics === true;

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (cookieDebugParam === "1") {
            window.localStorage.setItem(COOKIE_DEBUG_KEY, "1");
        }
        if (cookieDebugParam === "0") {
            window.localStorage.removeItem(COOKIE_DEBUG_KEY);
        }
    }, [cookieDebugParam]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const handler = () => setCookieSettingsOpen(true);
        window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handler);
        return () => {
            window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handler);
        };
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const onStart = (e) => {
            const name = e?.detail?.filename;
            setDownloadModalBody(
                name ? `Downloading ${name}…` : "Downloading your recording…"
            );
            setDownloadModalOpen(true);
            if (downloadModalTimer.current) {
                window.clearTimeout(downloadModalTimer.current);
            }
            downloadModalTimer.current = window.setTimeout(() => {
                setDownloadModalOpen(false);
            }, 5000);
        };
        window.addEventListener(DOWNLOAD_STARTED_EVENT, onStart);
        return () => {
            window.removeEventListener(DOWNLOAD_STARTED_EVENT, onStart);
            if (downloadModalTimer.current) {
                window.clearTimeout(downloadModalTimer.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!gaMeasurementId) return;
        const disableKey = `ga-disable-${gaMeasurementId}`;
        window[disableKey] = !analyticsAllowed;
    }, [gaMeasurementId, analyticsAllowed]);

    useEffect(() => {
        if (!gaMeasurementId) return;
        if (!analyticsAllowed) return;
        if (window.gtag) return;

        window.dataLayer = window.dataLayer || [];
        function gtag() {
            window.dataLayer.push(arguments);
        }
        window.gtag = gtag;
        window.gtag("js", new Date());
        window.gtag("config", gaMeasurementId);

        const existing = document.getElementById("ga-gtag");
        if (existing) return;

        const script = document.createElement("script");
        script.id = "ga-gtag";
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`;
        document.head.appendChild(script);
    }, [gaMeasurementId, analyticsAllowed]);

    useEffect(() => {
        if (!gaMeasurementId || !analyticsAllowed || !window.gtag) return;
        window.gtag("config", gaMeasurementId, {
            page_path: location.pathname + location.search,
        });
    }, [gaMeasurementId, analyticsAllowed, location.pathname, location.search]);

    const handleIdleLogout = useCallback(() => {
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        logout({ logoutParams: { returnTo: window.location.origin } });
    }, [logout]);

    const resetIdleTimer = useIdleLogout(
        handleIdleLogout,
        INACTIVITY_TIMEOUT_MS,
        LAST_ACTIVITY_KEY,
        isAuthenticated && !isLoading,
        showWarning
    );

    useEffect(() => {
        if (isLoading || !isAuthenticated) {
            setWarningMsLeft(null);
            return;
        }

        const readLastActivity = () => {
            const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
            const val = Number(raw);
            return Number.isFinite(val) && val > 0 ? val : Date.now();
        };

        const tick = () => {
            const msLeft = Math.max(
                0,
                readLastActivity() + INACTIVITY_TIMEOUT_MS - Date.now()
            );
            setWarningMsLeft(msLeft);
        };

        tick();
        const id = setInterval(tick, 1000);
        const onStorage = (e) => {
            if (e.key === LAST_ACTIVITY_KEY) tick();
        };
        window.addEventListener("storage", onStorage);
        return () => {
            clearInterval(id);
            window.removeEventListener("storage", onStorage);
        };
    }, [isAuthenticated, isLoading]);

    useEffect(() => {
        if (!showWarning) {
            document.body.classList.remove("pr-iw-lock");
            return;
        }
        document.body.classList.add("pr-iw-lock");
        return () => {
            document.body.classList.remove("pr-iw-lock");
        };
    }, [showWarning]);

    const authRoutes = [
        "/login",
        "/login/forgot-password/reset",
        "/sign-up",
        "/timeout",
    ];

    const isAuthRoute = authRoutes.includes(location.pathname);
    const isDashboard = location.pathname.startsWith("/dashboard");

    const removeMarginals = isAuthRoute || isDashboard;

    // const showCookieNotice =
    //     cookieDebug || !cookieConsent || cookieSettingsOpen;
    // const cookieSettingsOpenEffective = cookieDebug || cookieSettingsOpen;
    // const passwordGate = getPasswordGateStatus(location.pathname);
    // const suppressCookies = passwordGate.enabled && !passwordGate.unlocked;
    const [gateRequest, setGateRequest] = useState(null);
    const [forceGateOpen, setForceGateOpen] = useState(false);

    const performGateAction = useCallback(
        async (detail) => {
            const type = detail?.type || "auth0Login";
            if (type === "navigate") {
                const to = detail?.to || "/";
                navigate(to);
                return;
            }
            if (type === "auth0Login") {
                await loginWithRedirect(detail?.options || undefined);
                return;
            }
        },
        [navigate, loginWithRedirect]
    );

    useEffect(() => {
        if (typeof window === "undefined") return;
        const handler = (e) => {
            const detail = e?.detail || {};
            const status = getPasswordGateStatus(window.location.pathname);
            if (status.enabled && !status.unlocked) {
                setGateRequest(detail);
                setForceGateOpen(true);
                return;
            }
            performGateAction(detail);
        };
        window.addEventListener(PASSWORD_GATE_REQUEST_EVENT, handler);
        return () =>
            window.removeEventListener(PASSWORD_GATE_REQUEST_EVENT, handler);
    }, [performGateAction]);

    const saveCookieChoices = useCallback(
        ({ analytics }) => {
            const next = { analytics: analytics === true };
            writeCookieConsent(next);
            setCookieConsent(next);
            if (!next.analytics) clearAnalyticsCookies();
            setCookieSettingsOpen(false);
        },
        [setCookieConsent]
    );

    return (
        <div className="app">
            <PasswordGateway
                pathname={location.pathname}
                forceOpen={forceGateOpen}
                onRequestClose={() => {
                    setForceGateOpen(false);
                    setGateRequest(null);
                }}
                onUnlocked={() => {
                    const next = gateRequest;
                    setForceGateOpen(false);
                    setGateRequest(null);
                    if (next) performGateAction(next);
                }}
            >
                <DownloadingModal
                    open={downloadModalOpen}
                    title="Downloading"
                    body={downloadModalBody}
                />
                {!removeMarginals && <Navbar />}
                <div className="app-content">
                    <Outlet />
                </div>
                {!removeMarginals && <Footer />}
                {/* {showCookieNotice && !suppressCookies && (
                    <CookieNotice
                        analyticsAvailable={analyticsAvailable}
                        showClose={cookieSettingsOpenEffective}
                        initialAnalytics={cookieConsent?.analytics === true}
                        onClose={
                            cookieDebug ? undefined : () => setCookieSettingsOpen(false)
                        }
                        onConfirmChoices={cookieDebug ? undefined : saveCookieChoices}
                        onRejectAll={
                            cookieDebug
                                ? undefined
                                : () => saveCookieChoices({ analytics: false })
                        }
                        onAllowAll={
                            cookieDebug
                                ? undefined
                                : ({ analytics }) => saveCookieChoices({ analytics })
                        }
                    />
                )} */}
                {showWarning && (
                    <>
                        <div className="pr-iw__overlay" aria-hidden="true" />
                        <InactivityWarning
                            countdown={`${Math.floor(
                                (warningMsLeft || 0) / 60000
                            )}:${String(
                                Math.floor(
                                    ((warningMsLeft || 0) % 60000) / 1000
                                )
                            ).padStart(2, "0")}`}
                            msLeft={warningMsLeft}
                            onStay={() => {
                                resetIdleTimer?.();
                                setWarningMsLeft(INACTIVITY_TIMEOUT_MS);
                            }}
                        />
                    </>
                )}
            </PasswordGateway>
        </div>
    );
}

export default App;
